import {fetchCAPI} from '@github-ui/copilot-shared-ui/capi/fetch-capi'
import {taskByIdApiPath, tasksBatchApiPath} from '../utils/api-url-helpers'
import {useCAPIQuery} from '@github-ui/copilot-shared-ui/capi/use-capi-query'
import type {HydratedAPITask, RawHydratedAPITask} from '../types/api-task'
import {normalizeHydratedAPITask} from '../types/api-task'
import {useCallback, useEffect} from 'react'
import {useQueryClient} from '@github-ui/react-query'
import {useDebounce} from '@github-ui/use-debounce'
import {useAgentSessionsAlive, type AgentSessionAlivePayload} from './use-agent-sessions-alive'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {getInteractiveRefetchInterval, isNonTerminalInteractiveAPITask} from '../agents/interactive-polling'
import {syncTaskListsFromAPITask} from '../utils/task-helpers'

export const agentTaskQueryKey = (owner?: string, repo?: string, taskId?: string, apiURL?: string) => {
  const key = ['agent-task', taskId, owner, repo]
  return apiURL ? [...key, apiURL] : key
}

/**
 * Whether `queryKey` is a default-origin `agentTaskQueryKey` for `taskId`, with
 * any owner/repo. Keys for a custom API URL are excluded so a default-origin
 * update cannot leak into another origin's cache.
 */
export const isDefaultOriginAgentTaskQueryKey = (queryKey: readonly unknown[], taskId: string) =>
  queryKey.length === 4 && queryKey[0] === 'agent-task' && queryKey[1] === taskId

export const fetchTask = async ({taskId, signal, apiURL}: {taskId: string; signal?: AbortSignal; apiURL?: string}) => {
  const path = taskByIdApiPath(taskId)
  const response = await fetchCAPI({path, signal, ...(apiURL ? {copilotApiUrl: apiURL} : {})})
  const data = await response.json()
  // Handle both wrapped { task: {...} } (legacy) and unwrapped root-level response
  const raw = (data.task ?? data) as RawHydratedAPITask
  return normalizeHydratedAPITask(raw)
}

/**
 * Maximum number of task ids to request in a single batch call, kept in sync
 * with the server-side limit so the query string can never grow unbounded.
 */
export const TASK_IDS_BATCH_SIZE = 100

/**
 * Fetches the state of many tasks in as few requests as possible via the batch
 * route (`/agents/tasks/batch?task_ids=...`). Ids are sliced into chunks of
 * {@link TASK_IDS_BATCH_SIZE} and one request is issued per chunk.
 *
 * Partial-tolerant: the endpoint returns only the tasks it finds and omits
 * unknown ids, so a missing id never fails the batch — callers detect a "not
 * found" task by its absence from the returned array. Transport/HTTP errors
 * still reject so React Query can retry with backoff.
 */
export const fetchTasksBatch = async ({
  taskIds,
  signal,
}: {
  taskIds: string[]
  signal?: AbortSignal
}): Promise<HydratedAPITask[]> => {
  if (taskIds.length === 0) return []

  const chunks: string[][] = []
  for (let i = 0; i < taskIds.length; i += TASK_IDS_BATCH_SIZE) {
    chunks.push(taskIds.slice(i, i + TASK_IDS_BATCH_SIZE))
  }

  const chunkResults = await Promise.all(
    chunks.map(async chunk => {
      const path = tasksBatchApiPath(chunk)
      const response = await fetchCAPI({path, signal})
      const data = await response.json()
      // The batch route returns a TaskList ({ tasks: [...] }); tolerate a raw
      // array too in case the shape is unwrapped.
      const rawTasks = (Array.isArray(data) ? data : (data?.tasks ?? [])) as RawHydratedAPITask[]
      return rawTasks
    }),
  )

  return chunkResults.flat().map(normalizeHydratedAPITask)
}

export function useTask({
  owner,
  repo,
  taskId,
  initialTask,
  sessionsPollingInterval = 3000,
  copilotApiUrl,
  enabled = true,
}: {
  owner?: string
  repo?: string
  taskId?: string
  initialTask?: HydratedAPITask
  sessionsPollingInterval?: number
  copilotApiUrl?: string
  enabled?: boolean
}) {
  const queryClient = useQueryClient()
  const handleAliveUpdate = useCallback(
    (payload?: AgentSessionAlivePayload) => {
      // Only invalidate the task query if the update is related to the current task
      if (payload?.task_id === taskId) {
        queryClient.invalidateQueries({
          queryKey: agentTaskQueryKey(owner, repo, taskId, copilotApiUrl),
        })
      }
    },
    [queryClient, owner, repo, taskId, copilotApiUrl],
  )

  const debouncedHandleAliveUpdate = useDebounce(handleAliveUpdate, sessionsPollingInterval, {maxWait: 10000})

  const isTaskAliveEnabled = isFeatureEnabled('copilot_mission_control_task_alive_updates') && enabled && !!taskId

  useAgentSessionsAlive({
    onEvent: debouncedHandleAliveUpdate,
    enableCreateEvents: false,
    enableUpdateEvents: true,
    enableLogUpdates: false,
    enableTaskUpdates: true,
    enabled: isTaskAliveEnabled,
  })

  const query = useCAPIQuery<HydratedAPITask>({
    queryKey: agentTaskQueryKey(owner, repo, taskId, copilotApiUrl),
    queryFn: ({signal}) => {
      if (!taskId) {
        throw new Error('taskId is required')
      }

      return fetchTask({
        taskId,
        signal,
        apiURL: copilotApiUrl,
      })
    },
    initialData: initialTask,
    enabled: enabled && !!taskId,
    // CLI tasks need to be refetched more frequently to reflect real-time updates. We also only fetch them
    // when we know that the task is not "terminal", meaning that there could be more data coming in.
    refetchInterval: ({state: {data: task}}) => getInteractiveRefetchInterval(isNonTerminalInteractiveAPITask(task)),
    // The Remote CLI behavior is best suited being as close to real-time as possible,
    // so fetching fresh data on refocus helps.
    refetchOnWindowFocus: true,
  })
  const data = taskId ? query.data : initialTask

  // Propagate the latest task data into any cached task lists so they reflect
  // status updates from this query (e.g., CLI session state) without waiting
  // for their own refetch cycle.
  useEffect(() => {
    if (data) {
      syncTaskListsFromAPITask(queryClient, data)
    }
  }, [data, queryClient])

  return {
    data,
    dataUpdatedAt: query.dataUpdatedAt,
    isLoading: !!taskId && query.isLoading && !query.data,
    isError: !!taskId && query.isError,
    refetch: query.refetch,
  }
}
